// The tier-1 readout for the fast client: every sov beam in view, the tile
// it is taking, and the bracket of a staked contest (numbers.md §8, R116,
// ui-map-interface.md §6.5).
//
// It is `render/control.ts` with the PixiJS `Graphics` taken out. The look
// is unchanged and every figure still comes from
// data/presentation/presentation.toml (R312):
//
//   * a base's beam is ONE thin quiet line (`CONTROL_BASE_BEAM`);
//   * a Beacon's beam is THREE stacked strokes with `F_ADD`, so the overlap
//     blooms (`CONTROL_BEAM_TIERS`) — the engine's additive flag replaces
//     the second `Graphics` with `blendMode: 'add'`, and costs no second
//     draw call (the plan's A.1);
//   * the target tile wears a ring and an arc that closes as it falls;
//   * a travelling pip runs the beam on the `CONTROL_PIP_BASE_SEC` clock.
//
// Three batches, three draw calls, and nothing is tessellated per frame:
// the beams and the brackets are segments, the gauges are circles, and the
// pips are quads of the atlas `pip` cell.

import { cellCenterX, cellCenterY, factionColor, HEX_SIZE } from '../../world';
// The whole capture picture rides presentation.toml
// [presentation.control] (R312).
import {
  CONTROL_BASE_BEAM, CONTROL_BEAM_TIERS, CONTROL_CONTEST_BRACKET, CONTROL_CONTEST_R_K,
  CONTROL_GAUGE_ARC, CONTROL_GAUGE_RING, CONTROL_GAUGE_R_K, CONTROL_PIP,
  CONTROL_PIP_BASE_SEC, CONTROL_PULSE_BASE, CONTROL_PULSE_RATE,
  CONTROL_PULSE_SWING,
} from '../../gen/presentation';
import type { AtlasEntry } from '../gfx/atlas';
import { F_ADD, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, CircleSink, QuadSink, SegmentSink } from './batches';
import { cornerBrackets } from './shared';

/**
 * One beam as the client holds it, from the wire.
 *
 * The type is the PixiJS layer's own, imported and never copied: both
 * clients read one `State.beams` and must read it the same way. The import
 * is type-only, so no engine reaches this bundle.
 */
import type { UIBeam } from '../../render/control-types';

/** One clock for the beam, the ring and the arc (`time` in seconds). */
export function beamPulse(time: number): number {
  return CONTROL_PULSE_BASE + CONTROL_PULSE_SWING * Math.sin(time * CONTROL_PULSE_RATE);
}

export class ControlLayer implements Layer {
  readonly name = 'control';

  /** The beams as the overlay reads them (`ui.beams`). */
  beams: UIBeam[] = [];

  private sieges: { cell: number; faction: number }[] = [];
  private time = 0;
  private shown = true;

  private readonly segs: SegmentSink;
  private readonly circles: CircleSink;
  private readonly quads: QuadSink;
  private readonly pip: AtlasEntry;

  /**
   * The per-faction grouping, kept between frames. The PixiJS layer builds
   * a `Map` and two filtered arrays every frame; a layer here allocates
   * nothing in the steady state, so the map and its arrays are reused and
   * only their lengths are reset.
   */
  private readonly byFaction = new Map<number, UIBeam[]>();
  private readonly beacons: UIBeam[] = [];
  private readonly bases: UIBeam[] = [];

  constructor(src: BatchSource) {
    this.segs = src.newSegments(512);
    this.circles = src.newCircles(512);
    this.quads = src.newQuads(256);
    this.pip = src.sprite('pip');
  }

  /** The beams of this state frame. The array is kept, never copied. */
  apply(beams: UIBeam[]): void {
    this.beams = beams;
  }

  /** Objects under a scheduled contest, drawn as a hard bracket (§4 tier 2). */
  setSieges(list: { cell: number; faction: number }[]): void {
    this.sieges = list;
  }

  update(frame: FrameInfo): void {
    this.time += frame.dtMs / 1000;
    this.pack();
  }

  draw(ctx: DrawCtx): void {
    this.segs.flush(ctx.cam);
    this.circles.flush(ctx.cam);
    this.quads.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  // -- packing ---------------------------------------------------------------

  private pack(): void {
    this.segs.begin();
    this.circles.begin();
    this.quads.begin();
    const pulse = beamPulse(this.time);
    this.packSieges(pulse);
    if (this.beams.length === 0) return;

    // Grouped by faction, and inside a faction by whether it is a beacon:
    // the PixiJS layer needs the grouping because one stroke serves a whole
    // group; this one keeps it so the instance order is the same paint
    // order, which is what makes the two pictures identical.
    for (const list of this.byFaction.values()) list.length = 0;
    for (const b of this.beams) {
      const list = this.byFaction.get(b.faction);
      if (list) list.push(b);
      else this.byFaction.set(b.faction, [b]);
    }

    const r = HEX_SIZE * CONTROL_GAUGE_R_K;
    for (const [faction, list] of this.byFaction) {
      if (list.length === 0) continue;
      const color = factionColor(faction);
      this.bases.length = 0;
      this.beacons.length = 0;
      for (const b of list) (b.beacon ? this.beacons : this.bases).push(b);

      // The quiet half: a base's line, one thin stroke, no bloom.
      const baseAlpha = CONTROL_BASE_BEAM.alpha0 + CONTROL_BASE_BEAM.alpha1 * pulse;
      for (const b of this.bases) this.line(b, CONTROL_BASE_BEAM.width, color, baseAlpha, 0);
      // The loud half: a beacon's line, three widths, additive.
      for (const tier of CONTROL_BEAM_TIERS) {
        const alpha = tier.alpha * (tier.pulses ? pulse : 1);
        for (const b of this.beacons) this.line(b, tier.width, color, alpha, F_ADD);
      }

      // The target ring, then the arc that closes as the tile falls.
      for (const b of list) {
        const x = cellCenterX(b.targetCell), y = cellCenterY(b.targetCell);
        this.circles.ring(x, y, r, CONTROL_GAUGE_RING.width, color, CONTROL_GAUGE_RING.alpha * pulse);
      }
      const arcAlpha = CONTROL_GAUGE_ARC.alpha0 + CONTROL_GAUGE_ARC.alpha1 * pulse;
      for (const b of list) {
        const sweep = (b.progress / 100) * Math.PI * 2;
        if (sweep <= 0) continue;
        const x = cellCenterX(b.targetCell), y = cellCenterY(b.targetCell);
        this.circles.arc(x, y, r, CONTROL_GAUGE_ARC.width, -Math.PI / 2, sweep, color, arcAlpha);
      }

      for (const b of list) this.packPip(b, color);
    }
  }

  private line(b: UIBeam, width: number, color: number, alpha: number, flags: number): void {
    const tx = cellCenterX(b.targetCell), ty = cellCenterY(b.targetCell);
    this.segs.line(b.x, b.y, tx, ty, width, color, alpha, flags);
  }

  /**
   * One travelling glow running source → target. The speed follows the
   * margin, and the phase is offset by source id so a row of beams does not
   * pulse in lockstep.
   */
  private packPip(b: UIBeam, color: number): void {
    const tx = cellCenterX(b.targetCell), ty = cellCenterY(b.targetCell);
    const margin = Math.max(0.5, b.margin10 / 10);
    const period = CONTROL_PIP_BASE_SEC / margin;
    const phase = ((this.time / period) + (b.sourceId % 97) / 97) % 1;
    const alpha = (b.beacon ? CONTROL_PIP.beaconAlpha : CONTROL_PIP.baseAlpha) * Math.sin(phase * Math.PI);
    const scale = b.beacon ? CONTROL_PIP.beaconScale : CONTROL_PIP.baseScale;
    this.quads.sprite(
      b.x + (tx - b.x) * phase, b.y + (ty - b.y) * phase,
      0, this.pip, color, alpha, scale,
    );
  }

  /**
   * A staked contest gets four hard corner brackets in the attacker's
   * colour — visible to everyone, because a stake is a public commitment.
   */
  private packSieges(pulse: number): void {
    const r = HEX_SIZE * CONTROL_CONTEST_R_K;
    const { arm, width, alpha0, alpha1 } = CONTROL_CONTEST_BRACKET;
    const alpha = alpha0 + alpha1 * pulse;
    for (const s of this.sieges) {
      cornerBrackets(this.segs, cellCenterX(s.cell), cellCenterY(s.cell), r, arm, width,
        factionColor(s.faction), alpha);
    }
  }
}
